import _ from 'lodash';
import { observable, computed, makeObservable } from 'mobx';
import { UserInfo, permissionArrayObject, GranteeInfo } from '../model';

export class AccountStore {
  @observable isLoading = false;
  @observable displayName: string | null = '';
  @observable private id: string | null = '';
  @observable private token: string | undefined;
  @observable email: string | null = '';
  @observable private name: string | null = '';
  @observable app: string | null = '';

  @observable private createdAt: string | null = '';
  @observable private lastLoginAt: string | null = '';
  @observable updatedAt: string | null = '';

  @observable permissions: string[] | null = null;
  @observable parentAccountId: string | null = '';

  @observable private loginId: string | undefined;
  @observable private masterToken: string | undefined;
  @observable loginEmail: string | null = '';
  @observable loginPermissions: string[] | null = null;
  @observable company: string | null = null;
  @observable granteesList: GranteeInfo[] = [];

  constructor() {
    makeObservable(this);
  }

  @computed get allowImpersonate(): boolean {
    if (this.loginPermissions && this.loginPermissions.length > 0) {
      return _.some(
        this.loginPermissions,
        (p) => p === permissionArrayObject.management,
      );
    }
    return false;
  }

  @computed get isImpersonate(): boolean {
    return this.id !== this.loginId;
  }

  @computed get getLoginId(): string | undefined {
    if (this.loginId) {
      return this.loginId;
    }
    return undefined;
  }

  @computed get getToken(): string | undefined {
    if (this.token) {
      return this.token;
    }
    return undefined;
  }

  @computed get getMasterToken(): string | undefined {
    if (this.masterToken) {
      return this.masterToken;
    }
    return undefined;
  }

  @computed get userName(): string | undefined {
    if (this.name) {
      return this.name;
    }
    return undefined;
  }

  @computed get userId(): string | undefined {
    if (this.id) {
      return this.id;
    }
    return undefined;
  }

  @computed get companyName(): string | undefined {
    if (this.company) {
      return this.company;
    }
    return undefined;
  }

  @computed get isAdmin(): boolean {
    return this.parentAccountId === null;
  }
  @computed get allowDeviceDisplay(): boolean {
    return this.checkHavePermission(permissionArrayObject.deviceDisplay);
  }
  @computed get allowDeviceControl(): boolean {
    return this.checkHavePermission(permissionArrayObject.deviceControl);
  }
  @computed get allowGroupEdit(): boolean {
    return this.checkHavePermission(permissionArrayObject.groupEdit);
  }
  @computed get allowGroupDelete(): boolean {
    return this.checkHavePermission(permissionArrayObject.groupDelete);
  }
  @computed get allowHomeQR(): boolean {
    return this.checkHavePermission(permissionArrayObject.homeQR);
  }
  @computed get allowHomeInit(): boolean {
    return this.checkHavePermission(permissionArrayObject.homeInit);
  }
  @computed get allowHomeEdit(): boolean {
    return this.checkHavePermission(permissionArrayObject.homeEdit);
  }
  @computed get allowHomeMove(): boolean {
    return this.checkHavePermission(permissionArrayObject.homeMove);
  }
  @computed get allowHomeDelete(): boolean {
    return this.checkHavePermission(permissionArrayObject.homeDelete);
  }
  @computed get allowNotificationAll(): boolean {
    return this.checkHavePermission(permissionArrayObject.notificationAll);
  }
  @computed get isDoctor(): boolean {
    return _.some(this.permissions, (p) => p === permissionArrayObject.doctor);
  }
  @computed get isDrugStore(): boolean {
    return _.some(this.permissions, (p) => p === permissionArrayObject.drug);
  }
  @computed get isManagement(): boolean {
    return _.some(
      this.permissions,
      (p) => p === permissionArrayObject.management,
    );
  }
  @computed get isHydro(): boolean {
    return _.some(this.permissions, (p) => p === permissionArrayObject.hydro);
  }
  @computed get isAccountManager(): boolean {
    return _.some(
      this.permissions,
      (p) => p === permissionArrayObject.accountManager,
    );
  }
  @computed get isMamoleo(): boolean {
    return _.some(this.permissions, (p) => p === permissionArrayObject.mamoleo);
  }
  @computed get isCompanyAccountAdmin(): boolean {
    return _.some(
      this.permissions,
      (p) => p === permissionArrayObject.sekiadmin,
    );
  }
  // @computed get allowNotificationEach(): boolean {
  //   return this.checkHavePermission(permissionArrayObject.notificationEach);
  // }

  checkHavePermission(permission: string): boolean {
    if (this.permissions === null || this.isAdmin) {
      return true;
    }
    return _.some(this.permissions, (p) => p === permission);
  }

  updateUserName(name: string) {
    this.name = name;
  }

  loginSuccess(loginUser: UserInfo) {
    this.id = loginUser.id;
    this.token = loginUser.token;
    this.email = loginUser.email;
    this.name = loginUser.name;
    this.app = loginUser.app;
    this.permissions = loginUser.permissions || [];

    this.loginId = loginUser.id;
    this.masterToken = loginUser.token;
    this.loginEmail = loginUser.email;
    this.loginPermissions = loginUser.permissions || null;
    this.company = loginUser.company;

    this.createdAt = loginUser.createdAt;
    this.lastLoginAt = loginUser.lastLoginAt;
    this.updatedAt = loginUser.updatedAt;
  }

  spoofLoginSuccess(loginUser: UserInfo) {
    this.id = loginUser.id;
    this.token = loginUser.token;
    this.email = loginUser.email;
    this.name = loginUser.name;
    this.app = loginUser.app;
    this.permissions = loginUser.permissions || [];
    this.company = loginUser.company;

    this.createdAt = loginUser.createdAt;
    this.lastLoginAt = loginUser.lastLoginAt;
    this.updatedAt = loginUser.updatedAt;
  }

  clear() {
    this.isLoading = false;
    this.logout();
  }

  logoutImpersonate() {
    this.id = this.getLoginId || '';
    this.token = this.getMasterToken;
    this.email = this.loginEmail;
    this.name = this.userName || '';
    this.app = this.app || '';
    this.permissions = this.loginPermissions || [];
  }

  logout() {
    this.id = null;
    this.token = undefined;
    this.email = null;
    this.loginEmail = null;
    this.name = null;
    this.app = null;
    this.permissions = [];
    this.company = null;
    this.loginPermissions = null;
    this.parentAccountId = '';
    this.masterToken = undefined;
    this.loginId = undefined;
    this.granteesList = [];

    this.createdAt = null;
    this.lastLoginAt = null;
    this.updatedAt = null;
  }
}

const accountStore = new AccountStore();

export default accountStore;
